import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import PropTypes from "prop-types";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || window.location.origin;

const getInitials = (name = "") => {
  const initials = String(name)
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("");

  return initials.toUpperCase() || "C";
};

const normalizeMediaUrl = (value) => {
  const url = String(value || "").trim();

  if (!url || /^(https?:|blob:|data:)/i.test(url)) {
    return url;
  }

  try {
    const origin = new URL(API_BASE_URL).origin;
    return new URL(url, `${origin}/`).href;
  } catch {
    return url;
  }
};

const normalizeLinkedinUrl = (value) => {
  const url = String(value || "").trim();

  if (!url) {
    return "";
  }

  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
};

const DetailMetric = ({ icon, label, value }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 150,
      p: 1.5,
      borderRadius: "10px",
      bgcolor: "#F7F8FB",
      border: "1px solid #EAECF0",
      display: "flex",
      alignItems: "center",
      gap: 1.25,
    }}
  >
    <Box
      sx={{
        width: 34,
        height: 34,
        borderRadius: "8px",
        bgcolor: "#fff",
        color: "#292E5C",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {icon}
    </Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          color: "#667085",
          fontSize: 11,
          fontFamily: "Montserrat, sans-serif",
          fontWeight: 500,
          lineHeight: "16px",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          color: "#171717",
          fontSize: 14,
          fontFamily: "Montserrat, sans-serif",
          fontWeight: 600,
          lineHeight: "20px",
          overflowWrap: "anywhere",
        }}
      >
        {value}
      </Typography>
    </Box>
  </Box>
);

DetailMetric.propTypes = {
  icon: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
};

const CouncilDetailDialog = ({ counsellor, onClose, open }) => {
  const counsellorName = counsellor?.full_name || counsellor?.name || "Counsellor";
  const imageUrl = normalizeMediaUrl(counsellor?.photo);
  const linkedinUrl = normalizeLinkedinUrl(counsellor?.linkedin);
  const experience = counsellor?.years_of_experience
    ? `${counsellor.years_of_experience} years`
    : "Not added";
  const city = counsellor?.city || "Not added";
  const description =
    counsellor?.description ||
    "No detailed description is available for this counsellor yet.";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: "16px",
          overflow: "hidden",
          bgcolor: "#fff",
          boxShadow: "0px 16px 75px rgba(0, 0, 0, 0.08)",
          maxHeight: "88vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          px: { xs: 1.5, sm: 2 },
          py: 0.70,
          bgcolor: "#292E5C",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Typography
          sx={{
            color: "#fff",
            fontSize: 16,
            fontFamily: "Montserrat, sans-serif",
            fontWeight: 600,
            lineHeight: "26px",
          }}
        >
          Counsellor Details
        </Typography>
        <IconButton
          type="button"
          aria-label="Close counsellor details"
          onClick={onClose}
          sx={{ color: "#fff" }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          p: { xs: 2.5, sm: 4 },
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "240px 1fr" },
            gap: { xs: 1.5, sm: 2 },
            alignItems: "start",
            flex: 1,
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              borderRadius: "14px",
              bgcolor: "#F3F4F7",
              border: "1px solid #EAECF0",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                height: 240,
                bgcolor: "#E7E7EA",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {imageUrl ? (
                <Box
                  component="img"
                  src={imageUrl}
                  alt={counsellorName}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    objectPosition: "center",
                    display: "block",
                  }}
                />
              ) : (
                <Avatar
                  sx={{
                    width: 82,
                    height: 82,
                    bgcolor: "#292E5C",
                    color: "#fff",
                    fontFamily: "Montserrat, sans-serif",
                    fontWeight: 700,
                    fontSize: 26,
                  }}
                >
                  {getInitials(counsellorName)}
                </Avatar>
              )}
            </Box>

            <Box sx={{ p: 2 }}>
              <Typography
                sx={{
                  color: "#171717",
                  fontSize: 18,
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 700,
                  lineHeight: "24px",
                }}
              >
                {counsellorName}
              </Typography>
              {linkedinUrl ? (
                <Button
                  component="a"
                  href={linkedinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  startIcon={<LinkedInIcon sx={{ fontSize: 18 }} />}
                  sx={{
                    mt: 1.5,
                    px: 1.5,
                    py: 0.75,
                    borderRadius: "8px",
                    bgcolor: "#fff",
                    border: "1px solid #D9DEEA",
                    color: "#0A66C2",
                    fontSize: 12,
                    fontFamily: "Montserrat, sans-serif",
                    fontWeight: 600,
                    textTransform: "none",
                    "&:hover": {
                      bgcolor: "#fff",
                      borderColor: "#0A66C2",
                    },
                  }}
                >
                  LinkedIn
                </Button>
              ) : null}
            </Box>
          </Box>

          <Stack spacing={1.25} sx={{ minHeight: 0 }}>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1.5,
              }}
            >
              <DetailMetric
                icon={<WorkOutlineOutlinedIcon sx={{ fontSize: 20 }} />}
                label="Experience"
                value={experience}
              />
              <DetailMetric
                icon={<LocationOnOutlinedIcon sx={{ fontSize: 20 }} />}
                label="City"
                value={city}
              />
            </Box>

            <Box
              sx={{
                p: { xs: 2, sm: 2.5 },
                borderRadius: "12px",
                bgcolor: "#fff",
                border: "1px solid #EAECF0",
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Typography
                sx={{
                  color: "#171717",
                  fontSize: 15,
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 700,
                  lineHeight: "22px",
                  mb: 1.25,
                  flexShrink: 0,
                }}
              >
                Full Description
              </Typography>
              <Box
                sx={{
                  flex: 1,
                  minHeight: { xs: 180, md: 260 },
                  maxHeight: { xs: "38vh", md: "45vh" },
                  overflowY: "auto",
                  pr: 1,
                }}
              >
                <Typography
                  sx={{
                    color: "#444444",
                    fontSize: 13,
                    fontFamily: "Montserrat, sans-serif",
                    fontWeight: 400,
                    lineHeight: "21px",
                    whiteSpace: "pre-line",
                  }}
                >
                  {description}
                </Typography>
              </Box>
            </Box>
          </Stack>
        </Box>
      </DialogContent>

      <Box
        sx={{
          px: { xs: 1.5, sm: 2 },
          py: 1,
          // borderTop: "1px solid #EAECF0",
          bgcolor: "#fff",
          display: "flex",
          justifyContent: "flex-end",
          flexShrink: 0,
        }}
      >
        <Button
          type="button"
          variant="contained"
          onClick={onClose}
          sx={{
            px: 3,
            py: 1,
            bgcolor: "#F7AA47",
            borderRadius: "8px",
            color: "#fff",
            fontSize: 14,
            fontFamily: "Montserrat, sans-serif",
            fontWeight: 600,
            textTransform: "none",
            boxShadow: "none",
            "&:hover": {
              bgcolor: "#ED9B36",
              boxShadow: "none",
            },
          }}
        >
          Close
        </Button>
      </Box>
    </Dialog>
  );
};

CouncilDetailDialog.propTypes = {
  counsellor: PropTypes.shape({
    full_name: PropTypes.string,
    name: PropTypes.string,
    photo: PropTypes.string,
    linkedin: PropTypes.string,
    description: PropTypes.string,
    years_of_experience: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    city: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
};

CouncilDetailDialog.defaultProps = {
  counsellor: null,
};

export default CouncilDetailDialog;
